import React, { Fragment, useCallback, useMemo } from 'react';

import { TariffVerificationTypeEnum } from '@src/core/buyerApiClient';
import { dateMaskToIso } from '@src/common/dateMask';

import VerificationInput from '@src/domains/tariffs/components/VerificationInput';
import { useFieldKey } from '@src/domains/products/features/productAdder/hooks/useFieldKey';
import { useTariffVerificationErrorHandling } from '@src/domains/products/features/productAdder/hooks/useTariffVerificationErrorHandling';
import { ProductAdderTariffVerificationFieldProps } from '@src/domains/products/features/productAdder/types/tariffVerification';

import { VerificationFieldHelperText, VerificationFieldWrapper } from './styles';

const ProductAdderTariffVerificationField: React.FC<ProductAdderTariffVerificationFieldProps> = ({
  tariffTypeVerification,
  tariffUnitIndex,
  getFieldError,
  getCartTariffVerificationError,
  handleValueChange,
  initialValue,
}) => {
  const fieldKey = useFieldKey(tariffTypeVerification, tariffUnitIndex);

  const hasFieldError = getFieldError(tariffTypeVerification.tariffVerificationId);
  const hasCartTariffVerificationError = getCartTariffVerificationError();

  const { errorMessage, shouldShowError } = useTariffVerificationErrorHandling(
    hasFieldError,
    hasCartTariffVerificationError,
    tariffTypeVerification.tariffVerification.type,
  );

  const handleValueChangeWrapper = useCallback(
    (value: string) => {
      handleValueChange(tariffTypeVerification.tariffVerificationId, value);
    },
    [handleValueChange, tariffTypeVerification.tariffVerificationId],
  );

  const handleDateValue = useCallback(
    (value: string) => {
      const iso = dateMaskToIso(value);
      if (iso) handleValueChange(tariffTypeVerification.tariffVerificationId, iso);
    },
    [handleValueChange, tariffTypeVerification.tariffVerificationId],
  );

  const renderInput = useMemo(() => {
    const { type, inputLabel } = tariffTypeVerification.tariffVerification;
    if (type !== TariffVerificationTypeEnum.Date && type !== TariffVerificationTypeEnum.Document) {
      return null;
    }
    return (
      <Fragment>
        <VerificationInput
          fieldKey={fieldKey}
          initialValue={initialValue}
          inputLabel={inputLabel as string}
          onValueChange={type === TariffVerificationTypeEnum.Date ? handleDateValue : handleValueChangeWrapper}
          type={type}
          hasError={shouldShowError}
          errorMessage={errorMessage}
        />
        <VerificationFieldHelperText>
          {tariffTypeVerification.tariffVerification.helperText}
        </VerificationFieldHelperText>
      </Fragment>
    );
  }, [
    fieldKey,
    initialValue,
    handleDateValue,
    handleValueChangeWrapper,
    shouldShowError,
    errorMessage,
    tariffTypeVerification.tariffVerification,
  ]);

  return <VerificationFieldWrapper>{renderInput}</VerificationFieldWrapper>;
};

export default ProductAdderTariffVerificationField;
